import { db } from '@/core/db/dexie'
import { syncEndpoints } from '@/core/api/endpoints'
import { ApiError } from '@/core/api/fetchClient'
import type { OutProducao } from '@/core/db/entities'
import type { ProducaoRegistroDto } from '@/types/dtos'

/**
 * Push das linhas pendentes de `out_producao` para `POST /sync/producao`.
 *
 * Foreground sync — chamado após gravar uma sessão e em eventos
 * (online/visibilitychange/focus, ver Fase 6/7). Atualiza o `syncStatus` por linha
 * conforme a resposta granular do backend (`accepted` / `rejected`).
 *
 * Limites do backend: máximo 200 registros por chamada (ver `producaoBatchSchema`).
 */

const MAX_BATCH = 200

let runningPromise: Promise<PushSummary> | null = null

export interface PushSummary {
  attempted: number
  accepted: number
  rejected: number
  /** Reasons únicos relatados pelo backend (para diagnóstico). */
  reasons: string[]
  /** Última falha de rede, se houver. */
  networkError?: string
}

export async function pushProducaoOutbox(): Promise<PushSummary> {
  if (runningPromise) return runningPromise
  runningPromise = doPush().finally(() => {
    runningPromise = null
  })
  return runningPromise
}

async function doPush(): Promise<PushSummary> {
  const summary: PushSummary = { attempted: 0, accepted: 0, rejected: 0, reasons: [] }

  // Recovery: linhas presas em SYNCING (tab fechada mid-batch) voltam pra PENDING.
  await db.out_producao
    .where('syncStatus')
    .equals('SYNCING')
    .modify({ syncStatus: 'PENDING' })

  // Pega até MAX_BATCH linhas pendentes (ou que falharam antes — retry).
  const pending = await db.out_producao
    .where('syncStatus')
    .anyOf('PENDING', 'FAILED')
    .limit(MAX_BATCH)
    .toArray()

  if (pending.length === 0) return summary

  summary.attempted = pending.length

  // Marca como SYNCING antes do envio para evitar duplicação em concurrent calls.
  const ids = pending.map((p) => p.id)
  await db.out_producao.bulkUpdate(
    ids.map((id) => ({ key: id, changes: { syncStatus: 'SYNCING' as const } })),
  )

  try {
    const registros = pending.map(outProducaoToDto)
    const res = await syncEndpoints.producao(registros)

    const now = Date.now()
    const acceptedIds = new Set(res.accepted.map((a) => a.id))
    const rejectedMap = new Map(res.rejected.map((r) => [r.id, r.reason]))

    const updates = pending.map((p) => {
      if (acceptedIds.has(p.id)) {
        return {
          key: p.id,
          changes: {
            syncStatus: 'SYNCED' as const,
            lastSyncAttempt: now,
            failureReason: null,
          },
        }
      }
      const reason = rejectedMap.get(p.id) ?? 'UNKNOWN'
      return {
        key: p.id,
        changes: {
          syncStatus: 'FAILED' as const,
          lastSyncAttempt: now,
          failureReason: reason,
          retryCount: (p.retryCount ?? 0) + 1,
        },
      }
    })
    await db.out_producao.bulkUpdate(updates)

    summary.accepted = res.accepted.length
    summary.rejected = res.rejected.length
    summary.reasons = [...new Set(res.rejected.map((r) => r.reason))]
    return summary
  } catch (err) {
    // Erro de rede ou HTTP 5xx — volta tudo para PENDING (não consome retry).
    const now = Date.now()
    await db.out_producao.bulkUpdate(
      pending.map((p) => ({
        key: p.id,
        changes: {
          syncStatus: 'PENDING' as const,
          lastSyncAttempt: now,
          failureReason: err instanceof Error ? err.message : 'erro desconhecido',
        },
      })),
    )
    summary.networkError =
      err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message
    return summary
  }
}

function outProducaoToDto(o: OutProducao): ProducaoRegistroDto {
  return {
    id: o.id,
    projetoId: o.projetoId,
    servicoExecutadoId: o.servicoExecutadoId,
    servicoExecutadoNome: o.servicoExecutadoNome,
    servicoExecutadoQtd: o.servicoExecutadoQtd,
    servicoExecutadoPrioridade: o.servicoExecutadoPrioridade,
    servicoExecutadoUnidadeId: o.servicoExecutadoUnidadeId,
    servicoExecutadoUnidadeNome: o.servicoExecutadoUnidadeNome,
    dt: o.dt,
    equipeId: o.equipeId,
    equipeNome: o.equipeNome,
    encarregadoId: o.encarregadoId,
    encarregadoNome: o.encarregadoNome,
    materialId: o.materialId,
    materialNome: o.materialNome,
    materialQtd: o.materialQtd,
    materialUnidadeId: o.materialUnidadeId,
    materialUnidadeNome: o.materialUnidadeNome,
    alturaMedia: o.alturaMedia,
    materialRedeId: o.materialRedeId,
    materialRedeNome: o.materialRedeNome,
    diametroId: o.diametroId,
    diametroNome: o.diametroNome,
    tpRedeId: o.tpRedeId,
    tpRedeNome: o.tpRedeNome,
    trechoId: o.trechoId,
    trechoNome: o.trechoNome,
    estaca: o.estaca,
    estacaInicial: o.estacaInicial,
    estacaFinal: o.estacaFinal,
    chuva: o.chuva,
    obs: o.obs,
    capturedAt: o.capturedAt,
    deletado: o.deletado,
  }
}
